import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { ForgotPassword } from '../components'
import { apiService } from '../services/api'

import { Helmet } from 'react-helmet-async'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const { isLoading, isAuthenticated } = useAuth()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (!isClient) return
    // Redirect authenticated users to dashboard
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, isLoading, navigate, isClient])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/fwo-logo.png" 
            alt="FlyWideOpen" 
            className="h-16 mx-auto mb-4 opacity-50 animate-pulse" 
          />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Forgot Password - FWO Pilot Portal</title>
        <meta name="description" content="Reset your password for the FlyWideOpen Pilot Portal" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-3">
                <img 
                  src="/fwo-logo.png" 
                  alt="FlyWideOpen" 
                  className="h-10" 
                />
                <h1 className="text-xl font-bold text-gray-900">Pilot Portal</h1>
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Reset Your Password
            </h2>
            <p className="text-lg text-gray-600">
              Enter your email address and we'll send you instructions to reset your password
            </p>
          </div>

          <ForgotPassword 
            onResetPassword={async (email: string) => {
              return await apiService.resetPassword(email)
            }}
          />

          {/* Navigation Links */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-fwo-blue hover:text-blue-700 underline"
            >
              Back to Login
            </Link>
          </div>

          {/* Help Section */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Reset Process</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-fwo-blue text-white rounded-full text-xs flex items-center justify-center mr-3 mt-0.5">1</span>
                  <p>Enter your registered email address and click "Send Reset Instructions"</p>
                </div>
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-fwo-blue text-white rounded-full text-xs flex items-center justify-center mr-3 mt-0.5">2</span>
                  <p>Check your email inbox for a message with a temporary password</p>
                </div>
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-fwo-blue text-white rounded-full text-xs flex items-center justify-center mr-3 mt-0.5">3</span>
                  <p>Use the temporary password to log in and set a new secure password</p>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">Important Notes:</h4>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• Only registered pilots from Airtribune can reset their password</li>
                  <li>• Temporary passwords expire after 24 hours</li>
                  <li>• Check your spam folder if you don't receive the email</li>
                  <li>• Contact competition organizers if you continue having issues</li>
                </ul>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center text-gray-600">
              <p>&copy; {new Date().getFullYear()} FlyWideOpen. All rights reserved.</p>
              <p className="mt-2 text-sm">
                Paragliding competition management platform
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}